import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Plus, Trash2, Pencil } from "lucide-react"
import { useState } from "react"
import { router } from "@inertiajs/react"
import type { Deal } from "@/types"

type Props = {
  projectId: string
  deals: Deal[]
}

const formatCurrency = (value: number | null) => {
  if (value === null || value === undefined) return "—"
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value)
}

export function DealsTab({ projectId, deals }: Props) {
  const [showForm, setShowForm] = useState(false)
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null)

  const { data, setData, post, patch, processing, reset } = useForm({
    estimated_revenue: "",
    estimated_costs: "",
    estimated_margin: "",
    actual_revenue: "",
    actual_costs: "",
    actual_margin: "",
    co_development: false,
    suspensive_conditions: "",
    permit_submission_date: "",
    permit_granted_date: "",
    notes: "",
  })

  const openEdit = (deal: Deal) => {
    setEditingDeal(deal)
    setData({
      estimated_revenue: deal.estimated_revenue?.toString() || "",
      estimated_costs: deal.estimated_costs?.toString() || "",
      estimated_margin: deal.estimated_margin?.toString() || "",
      actual_revenue: deal.actual_revenue?.toString() || "",
      actual_costs: deal.actual_costs?.toString() || "",
      actual_margin: deal.actual_margin?.toString() || "",
      co_development: deal.co_development,
      suspensive_conditions: deal.suspensive_conditions || "",
      permit_submission_date: deal.permit_submission_date || "",
      permit_granted_date: deal.permit_granted_date || "",
      notes: deal.notes || "",
    })
    setShowForm(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingDeal) {
      patch(`/projects/${projectId}/deals/${editingDeal.id}`, {
        onSuccess: () => {
          reset()
          setShowForm(false)
          setEditingDeal(null)
        },
      })
    } else {
      post(`/projects/${projectId}/deals`, {
        onSuccess: () => {
          reset()
          setShowForm(false)
        },
      })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Données financières</h3>
        <Button
          size="sm"
          onClick={() => {
            setEditingDeal(null)
            reset()
            setShowForm(!showForm)
          }}
        >
          <Plus className="size-4" />
          Ajouter
        </Button>
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          setShowForm(open)
          if (!open) setEditingDeal(null)
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingDeal ? "Modifier le deal" : "Nouveau deal"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
            <DialogBody className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>CA estimé</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={data.estimated_revenue}
                  onChange={(e) => setData("estimated_revenue", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Coûts estimés</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={data.estimated_costs}
                  onChange={(e) => setData("estimated_costs", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Marge estimée</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={data.estimated_margin}
                  onChange={(e) => setData("estimated_margin", e.target.value)}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>CA réel</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={data.actual_revenue}
                  onChange={(e) => setData("actual_revenue", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Coûts réels</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={data.actual_costs}
                  onChange={(e) => setData("actual_costs", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Marge réelle</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={data.actual_margin}
                  onChange={(e) => setData("actual_margin", e.target.value)}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Date dépôt permis</Label>
                <Input
                  type="date"
                  value={data.permit_submission_date}
                  onChange={(e) => setData("permit_submission_date", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Date obtention permis</Label>
                <Input
                  type="date"
                  value={data.permit_granted_date}
                  onChange={(e) => setData("permit_granted_date", e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={data.co_development} onCheckedChange={(v) => setData("co_development", v)} />
              <Label>Co-développement</Label>
            </div>
            <div className="space-y-2">
              <Label>Conditions suspensives</Label>
              <Textarea
                value={data.suspensive_conditions}
                onChange={(e) => setData("suspensive_conditions", e.target.value)}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={data.notes} onChange={(e) => setData("notes", e.target.value)} rows={2} />
            </div>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false)
                  setEditingDeal(null)
                }}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={processing}>
                {editingDeal ? "Mettre à jour" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {deals.length === 0 ? (
        <p className="text-muted-foreground text-sm">Aucune donnée financière</p>
      ) : (
        <div className="space-y-4">
          {deals.map((deal) => (
            <Card key={deal.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    Données financières
                    {deal.co_development && (
                      <span className="text-muted-foreground ml-2 font-normal">(Co-développement)</span>
                    )}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="size-7" onClick={() => openEdit(deal)}>
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      onClick={() => router.delete(`/projects/${projectId}/deals/${deal.id}`)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-muted-foreground text-xs mb-2">Estimé</p>
                    <div className="space-y-1 text-sm">
                      <p>CA : {formatCurrency(deal.estimated_revenue)}</p>
                      <p>Coûts : {formatCurrency(deal.estimated_costs)}</p>
                      <p className="font-medium">Marge : {formatCurrency(deal.estimated_margin)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-2">Réel</p>
                    <div className="space-y-1 text-sm">
                      <p>CA : {formatCurrency(deal.actual_revenue)}</p>
                      <p>Coûts : {formatCurrency(deal.actual_costs)}</p>
                      <p className="font-medium">Marge : {formatCurrency(deal.actual_margin)}</p>
                    </div>
                  </div>
                </div>
                {deal.notes && <p className="text-sm text-muted-foreground mt-3">{deal.notes}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
